import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from './baseQueryWithAuth';

export interface Repository {
  id: number;
  full_name: string;
  name: string;
  language: string | null;
  private: boolean;
  default_branch: string;
  latest_debt_score: number | null;
  latest_scan_at: string | null;
  latest_scan_id: number | null;
  latest_score_security: number | null;
  latest_score_complexity: number | null;
  latest_score_dependencies: number | null;
  latest_score_coverage: number | null;
  latest_security_exposure_usd: number | null;
  latest_count_critical: number | null;
  latest_count_high: number | null;
  latest_count_medium: number | null;
  latest_count_low: number | null;
  latest_loc_total: number | null;
  repo_type: string | null;
  iac_framework: string | null;
  pr_gate_enabled: boolean | null;
}

export interface PrCheck {
  id: number;
  status: string;
  status_message: string | null;
  checked_pr_number: number | null;
  checked_pr_url: string | null;
  pr_gate_passed: boolean | null;
  count_critical: number | null;
  count_high: number | null;
  count_medium: number | null;
  count_low: number | null;
  created_at: string;
}

export interface AvailableRepository {
  github_repo_id: number;
  full_name: string;
  name: string;
  default_branch: string;
  language: string | null;
  private: boolean;
  size_kb: number;
}

export interface FileHealth {
  health_score: number;    // 0–100
  loc: number;             // non-empty line count
  issue_count: number;
  summary: string;
  recommendation: string;
}

// Phase 1a git-history signal — docs/moat-strategy.md. Computed by a
// separate async job (debtiq-backend's app/workers/churn_job.py) after the
// scan already shows "complete"; may be absent/empty on scans from before
// this shipped or while still computing (see Scan.churn_status below).
export interface ChurnFileSignal {
  commit_count: number;
  author_count: number;
  last_commit_days_ago: number;
  co_changed_with: { file: string; count: number }[];
  hotspot_score: number;        // 0-1 percentile rank among touched files in this scan
  bus_factor_risk: number | null;
  // Hotspot-weighted "cost to ignore" for this file's complexity/dead_code/
  // documentation debt — risk-weighted (fix cost × a bounded hotspot
  // multiplier), deliberately not a literal annual projection. Absent when
  // the file has no open maintainability issues to base a fix cost on.
  // See debtiq-backend's app/core/git_churn.py:compute_maintainability_risk_usd.
  maintainability_risk_usd?: number;
}

export interface LoadProfileFinding {
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  detail: string;
  recommendation: string;
}

export interface LoadProfile {
  concurrency_model: 'async' | 'threaded' | 'process' | 'mixed' | 'unknown';
  bottleneck_type: 'cpu' | 'db' | 'memory' | 'io_network' | 'mixed' | 'unknown';
  worker_type: string;
  detected_workers: number | null;
  db_pool_size: number | null;
  db_max_overflow: number | null;
  db_pool_timeout_s: number | null;
  has_caching: boolean;
  sync_blocking_detected: boolean;
  missing_pagination_detected: boolean;
  key_files_found: string[];
  findings: LoadProfileFinding[];
  summary: string;
}

// Real fingerprint-based diff between two scans — see app/api/scans.py's
// /{scan_id}/compare/{baseline_id}. Replaces a client-side guessed formula.
export interface CompareScansResult {
  resolved_count: number;
  new_count: number;
  changed_count: number;
  unchanged_count: number;
}

export interface ScanHistoryItem {
  id: number;
  debt_score: number | null;
  score_security: number | null;
  score_complexity: number | null;
  score_coverage: number | null;
  score_documentation: number | null;
  score_dependencies: number | null;
  count_critical: number;
  count_high: number;
  count_medium: number;
  count_low: number;
  created_at: string;
  trigger: string;
  branch: string;
  commit_sha: string | null;
}

// Weight keys match app/core/scan_scorer.py's DEBT_SCORE_WEIGHTS exactly — this is the real
// formula, served as data specifically so the "how is this calculated" UI can't hardcode a
// description that drifts from it. See docs/scan-report-integrity-plan.md, KG-04.
export interface ScoreFormula {
  weights: Record<string, number>;
  version: string;
}

export interface Scan {
  id: number;
  repository_id: number;
  status: string;
  status_message: string | null;
  trigger: string;
  branch: string;
  commit_sha: string | null;
  debt_score: number | null;
  score_complexity: number | null;
  score_security: number | null;
  score_coverage: number | null;
  score_documentation: number | null;
  score_dependencies: number | null;
  score_dead_code: number | null;
  score_formula: ScoreFormula | null;
  count_critical: number;
  count_high: number;
  count_medium: number;
  count_low: number;
  ai_repo_summary: string | null;
  ai_savings_headline: string | null;
  ai_top_priority_ids: number[] | null;
  loc_total: number | null;
  loc_by_language: Record<string, number> | null;
  load_profile: LoadProfile | null;
  file_health: Record<string, FileHealth> | null;
  ai_enriched_count: number | null;
  fix_pr_url: string | null;
  fix_pr_number: number | null;
  fix_summary: string | null;
  scan_started_at: string | null;
  scan_completed_at: string | null;
  ai_completed_at: string | null;
  carbon_kwh_total: number | null;
  carbon_co2g_total: number | null;
  // Authoritative — sum of every issue's remediation_minutes, computed
  // backend-side (app/core/remediation_cost.py). Never recompute this
  // client-side; see calcBusinessImpact in pages/scans/utils.ts.
  remediation_cost_usd_total: number | null;
  remediation_cost_calibrated_fraction: number | null;
  // SQALE Technical Debt Ratio (%): remediation cost / (non-test LOC * 30
  // min/line dev-cost convention) * 100. See debtiq-backend's
  // app/core/remediation_cost.py:compute_debt_ratio_pct. null when the repo
  // has no non-test LOC to divide by — never render as 0%.
  debt_ratio_pct: number | null;
  // Phase 1a git-history signal (see ChurnFileSignal above). churn_status
  // distinguishes "still computing"/"not run yet" (null or "pending") from
  // "computed, no signal" — never treat a missing entry as zero hotspot.
  churn_analysis: Record<string, ChurnFileSignal> | null;
  churn_computed_at: string | null;
  churn_status: 'pending' | 'complete' | 'failed' | 'skipped' | null;
  // Scan-level sum of churn_analysis[file].maintainability_risk_usd.
  maintainability_risk_usd_total: number | null;
  issues: Issue[];
}

export interface Issue {
  id: number;
  category: string;
  severity: string;
  // Free-tier scan-results gating (2026-10-15) — true for issues beyond the
  // free plan's per-category visible count; every other field on a locked
  // issue is a server-side redacted placeholder (see backend's
  // app.core.issue_gating), not just hidden client-side. Absent/undefined
  // on older cached responses — always treat as unlocked in that case,
  // never as locked, so a stale cache can't accidentally hide real data.
  is_locked?: boolean;
  file_path: string;
  line_start: number | null;
  title: string;
  tool_detail: string | null;
  tool_name: string;
  ai_explanation: string | null;
  ai_priority_score: number | null;
  ai_priority_reasoning: string | null;
  ai_estimated_fix_time: string | null;  // display-only — never used for cost math, see remediation_minutes
  ai_fix_complexity: string | null;
  // Deterministic, per-rule (SQALE-style) — the sole source for any dollar
  // figure. See debtiq-backend's app/core/remediation_cost.py. Replaced a
  // client-side parse of ai_estimated_fix_time that computed $255.8M for a
  // ~5,000 line repo from a single malformed AI string (2026-10-21 incident).
  remediation_minutes: number | null;
  remediation_cost_source: 'sonarqube_equivalent' | 'provisional_estimate' | null;
  file_loc: number | null;
  perf_estimated_compute_saving: string | null;
  perf_estimated_memory_saving: string | null;
  perf_confidence: string | null;
  perf_notes: string | null;
  carbon_kwh_per_month: number | null;
  carbon_co2g_per_month: number | null;
  cve_id: string | null;
  epss_score: number | null;
  security_risk_usd: number | null;
  is_known_exploited: boolean | null;
  kev_date_added: string | null;
  kev_ransomware_use: boolean | null;
  owasp_category: string | null;  // e.g. "A03:2021-Injection"
  status: string;  // open | fix_generating | pr_raised | pr_merged | ignored
  fp_reason: string | null;  // false_positive | already_mitigated | wont_fix | not_relevant
  // Sandboxed fix verification — null means "not verified" (feature disabled, or no
  // relevant tests), never treat null the same as false. See docs/fix-verification-design.md.
  fix_verified: boolean | null;
  fix_verification_summary: string | null;
  fix_failure_reason: string | null;
  pr_url: string | null;
  pr_number: number | null;
  // Dependency intelligence
  affected_package: string | null;
  current_version: string | null;
  safe_version: string | null;
  cve_ids: string[] | null;
  max_cvss: number | null;
  is_direct_dependency: boolean | null;
  dep_chain: string[] | null;
  first_seen_at: string | null;
  days_in_main: number | null;
  policy_breach: boolean | null;
  conflict_safe: boolean | null;
  conflict_notes: string | null;
}

export interface ProposedModule {
  filename: string;
  responsibility: string;
  estimated_loc: number;
  classes: string[];
  functions: string[];
  constants: string[];
}

export interface RefactorPlanContent {
  summary: string;
  rationale: string;
  modules: ProposedModule[];
  migration_notes: string;
}

export interface RefactorPlan {
  id: number;
  scan_id: number;
  file_path: string;
  file_loc: number;
  language: string | null;
  status: string;
  plan_json: RefactorPlanContent | null;
  rejection_reason: string | null;
  failure_reason: string | null;
  regeneration_count: number;
  original_plan_id: number | null;
  pr_url: string | null;
  pr_number: number | null;
  issue_url: string | null;
  issue_number: number | null;
  created_at: string;
  confirmed_at: string | null;
  rejected_at: string | null;
  pr_raised_at: string | null;
  issue_raised_at: string | null;
}

export interface SbomDocument {
  bomFormat: string;
  specVersion: string;
  serialNumber: string;
  version: number;
  metadata: { timestamp: string; component: { type: string; name: string; version: string } };
  components: Array<{ type: string; 'bom-ref': string; name: string; version: string; purl?: string; scope?: string }>;
  vulnerabilities?: Array<{ id: string; affects: Array<{ ref: string }> }>;
}

// Supply-chain "weakest link" radar (2026-10-19) — entirely deterministic
// scoring computed server-side by app.core.sbom_risk from Trivy's native
// CycloneDX output. See risk_formula for the exact weights used, exposed
// directly rather than duplicated in a separate description, so the "how
// is this calculated" UI can never drift from the real formula.
export interface SbomRadarComponent {
  id: number;
  name: string;
  version: string | null;
  purl: string | null;
  ecosystem: string | null;
  is_direct: boolean;
  transitive_depth: number;
  blast_radius: number;
  cve_ids: string[] | null;
  max_cvss: number | null;
  epss_score: number | null;
  is_known_exploited: boolean;
  risk_score: number;
}

export interface SupplyChainRadar {
  components: SbomRadarComponent[];
  risk_formula: {
    weights: Record<string, number>;
    version: string;
  };
}

export interface Policy {
  id: number;
  repository_id: number | null;
  rule_kind: 'max_severity_count' | 'max_age_days' | 'min_category_score';
  category: 'security' | 'complexity' | 'dependencies' | 'documentation';
  params: Record<string, number | string>;
  action: 'block' | 'flag';
  enabled: boolean;
  tier: 'repo' | 'account' | 'global';
}

export interface PolicyWrite {
  repository_id?: number | null;
  rule_kind: string;
  category: string;
  params: Record<string, number | string>;
  action?: string;
  enabled?: boolean;
}

export type OnboardingStepState = 'complete' | 'current' | 'locked' | 'optional' | 'dismissed';
export type OnboardingStatus = 'active' | 'skipped' | 'completed';

export interface OnboardingStep {
  key: string;
  label: string;
  description: string;
  route: string;
  target: string | null;
  state: OnboardingStepState;
}

export interface OnboardingState {
  tutorial_version: string;
  status: OnboardingStatus;
  current_step: string | null;
  last_step_key: string | null;
  steps: OnboardingStep[];
  facts: {
    repo_count: number;
    scan_count: number;
    latest_completed_scan_id: number | null;
    pr_gate_enabled: boolean;
  };
}

export interface OnboardingUpdate {
  status?: OnboardingStatus;
  dismissed_step?: string;
  completed_step?: string;
  last_step_key?: string | null;
}

export const githubApi = createApi({
  reducerPath: 'githubApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Repos', 'Scans', 'RefactorPlans', 'Policies', 'Onboarding'],
  endpoints: (builder) => ({
    getRepos: builder.query<Repository[], void>({
      query: () => '/repos/',
      providesTags: ['Repos'],
    }),
    getAvailableRepos: builder.query<AvailableRepository[], void>({
      query: () => '/repos/available',
      providesTags: ['Repos'],
    }),
    connectRepo: builder.mutation<Repository, AvailableRepository>({
      query: (body) => ({
        url: '/repos/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Repos', 'Onboarding'],
    }),
    updateRepo: builder.mutation<{ id: number; pr_gate_enabled: boolean }, { repo_id: number; pr_gate_enabled: boolean }>({
      query: ({ repo_id, ...body }) => ({
        url: `/repos/${repo_id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Repos', 'Onboarding'],
    }),
    triggerScan: builder.mutation<{ scan_id: number }, { repo_id: number; branch?: string }>({
      query: ({ repo_id, branch }) => ({
        url: `/scans/${repo_id}`,
        method: 'POST',
        body: branch ? { branch } : {},
      }),
      invalidatesTags: ['Scans', 'Repos', 'Onboarding'],
    }),
    getScan: builder.query<Scan, number>({
      query: (scan_id) => `/scans/${scan_id}`,
      transformResponse: (response: { scan: Omit<Scan, 'issues'>; issues: Issue[] }) => ({
        ...response.scan,
        issues: response.issues,
      }),
      providesTags: ['Scans'],
    }),
    getScanSbom: builder.query<SbomDocument, number>({
      query: (scan_id) => `/scans/${scan_id}/sbom`,
    }),
    getSupplyChainRadar: builder.query<SupplyChainRadar, number>({
      query: (scan_id) => `/scans/${scan_id}/supply-chain-radar`,
    }),
    getScanHistory: builder.query<ScanHistoryItem[], number>({
      query: (repo_id) => `/scans/repo/${repo_id}/history`,
      providesTags: ['Scans'],
    }),
    compareScans: builder.query<CompareScansResult, { scanId: number; baselineId: number }>({
      query: ({ scanId, baselineId }) => `/scans/${scanId}/compare/${baselineId}`,
      providesTags: ['Scans'],
    }),
    getPrChecks: builder.query<PrCheck[], number>({
      query: (repo_id) => `/scans/repo/${repo_id}/pr-checks`,
      providesTags: ['Scans'],
    }),
    triggerFix: builder.mutation<
      { status: string; scan_id: number; issue_ids: number[]; skipped_ids: number[] },
      { scan_id: number; issue_ids: number[] }
    >({
      query: (body) => ({ url: '/issues/fix', method: 'POST', body }),
      invalidatesTags: ['Scans'],
    }),
    dismissIssue: builder.mutation<
      { status: string; issue_id: number; fp_reason: string },
      { issue_id: number; reason: string }
    >({
      query: ({ issue_id, reason }) => ({
        url: `/issues/${issue_id}/dismiss`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Scans'],
    }),
    listRefactorPlans: builder.query<RefactorPlan[], number>({
      query: (repository_id) => `/refactor/plans?repository_id=${repository_id}`,
      providesTags: ['RefactorPlans'],
    }),
    createRefactorPlan: builder.mutation<RefactorPlan, { scan_id: number; file_path: string }>({
      query: (body) => ({ url: '/refactor/plans', method: 'POST', body }),
      invalidatesTags: ['RefactorPlans'],
    }),
    confirmRefactorPlan: builder.mutation<RefactorPlan, number>({
      query: (plan_id) => ({ url: `/refactor/plans/${plan_id}/confirm`, method: 'POST' }),
      invalidatesTags: ['RefactorPlans'],
    }),
    rejectRefactorPlan: builder.mutation<RefactorPlan, { plan_id: number; reason?: string }>({
      query: ({ plan_id, reason }) => ({
        url: `/refactor/plans/${plan_id}/reject`,
        method: 'POST',
        body: { reason: reason ?? null },
      }),
      invalidatesTags: ['RefactorPlans'],
    }),
    getEffectivePolicies: builder.query<Policy[], number>({
      query: (repository_id) => `/policies/effective?repository_id=${repository_id}`,
      providesTags: ['Policies'],
    }),
    listMyPolicies: builder.query<Policy[], void>({
      query: () => '/policies/mine',
      providesTags: ['Policies'],
    }),
    createPolicy: builder.mutation<Policy, PolicyWrite>({
      query: (body) => ({ url: '/policies/', method: 'POST', body }),
      invalidatesTags: ['Policies'],
    }),
    updatePolicy: builder.mutation<Policy, { policy_id: number } & PolicyWrite>({
      query: ({ policy_id, ...body }) => ({
        url: `/policies/${policy_id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Policies'],
    }),
    deletePolicy: builder.mutation<{ status: string }, number>({
      query: (policy_id) => ({ url: `/policies/${policy_id}`, method: 'DELETE' }),
      invalidatesTags: ['Policies'],
    }),
    getOnboardingState: builder.query<OnboardingState, void>({
      query: () => '/onboarding/state',
      providesTags: ['Onboarding'],
    }),
    updateOnboardingState: builder.mutation<OnboardingState, OnboardingUpdate>({
      query: (body) => ({ url: '/onboarding/state', method: 'PATCH', body }),
      invalidatesTags: ['Onboarding'],
    }),
  }),
});

export const {
  useGetReposQuery,
  useGetAvailableReposQuery,
  useConnectRepoMutation,
  useUpdateRepoMutation,
  useTriggerScanMutation,
  useGetScanQuery,
  useLazyGetScanQuery,
  useGetScanHistoryQuery,
  useCompareScansQuery,
  useLazyGetScanSbomQuery,
  useGetSupplyChainRadarQuery,
  useGetPrChecksQuery,
  useTriggerFixMutation,
  useDismissIssueMutation,
  useListRefactorPlansQuery,
  useCreateRefactorPlanMutation,
  useConfirmRefactorPlanMutation,
  useRejectRefactorPlanMutation,
  useGetEffectivePoliciesQuery,
  useListMyPoliciesQuery,
  useCreatePolicyMutation,
  useUpdatePolicyMutation,
  useDeletePolicyMutation,
  useGetOnboardingStateQuery,
  useUpdateOnboardingStateMutation,
} = githubApi;
